// Dynamic UI theming — token definitions, CSS-variable injection, presets.
// Tokens are produced by the Platform Console Theme Studio and resolved per
// tenant by the backend (`GET /theme/active`). Keys MUST stay in sync with
// backend/services/theme.py:DEFAULT_TOKENS.

export const DEFAULT_TOKENS = {
  // Surfaces
  canvas: "#0A0F1C",
  surface: "#111A2E",
  surface2: "#0E1626",
  surface3: "#172339",
  surface4: "#1E2B45",
  sidebar: "#0B1E3F",
  sidebarText: "#E6EBF3",
  inputBg: "#0E1626",
  border: "#22304D",
  cardBorder: "",
  // Surface opacity & effects
  canvasOpacity: "1",
  surfaceOpacity: "1",
  sidebarOpacity: "1",
  glass: "0",
  density: "1",
  // Text
  textPrimary: "#F1F5F9",
  textSecondary: "#CBD5E1",
  textMuted: "#8B97AD",
  // Accent & status
  accent: "#2563EB",
  accent2: "#06B6D4",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#F43F5E",
  // Links & icons
  navActive: "",
  link: "",
  focusRing: "",
  iconColor: "",
  // Typography
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  headingFont: "",
  fontScale: "1",
  headingWeight: "700",
  titleSize: "1.0625rem",
  subtitleSize: "0.8125rem",
  letterSpacing: "0em",
  lineHeight: "1.5",
  // Buttons
  buttonStyle: "solid",
  buttonTextSize: "0.875rem",
  buttonWeight: "600",
  buttonRadius: "0.5rem",
  buttonPadY: "0.5rem",
  buttonPadX: "1rem",
  // Shape & depth
  radius: "0.75rem",
  borderWidth: "1px",
  shadowStrength: "1",
  // Background image
  bgImage: "",
};

// token key -> CSS custom property (string/numeric tokens only).
// buttonStyle + glass are applied as classes (see applyThemeVars).
export const TOKEN_TO_VAR = {
  canvas: "--cc-canvas",
  surface: "--cc-surface",
  surface2: "--cc-surface-2",
  surface3: "--cc-surface-3",
  surface4: "--cc-surface-4",
  sidebar: "--cc-sidebar",
  sidebarText: "--cc-sidebar-text",
  inputBg: "--cc-input-bg",
  border: "--cc-border",
  cardBorder: "--cc-card-border",
  canvasOpacity: "--cc-canvas-opacity",
  surfaceOpacity: "--cc-surface-opacity",
  sidebarOpacity: "--cc-sidebar-opacity",
  density: "--cc-density",
  textPrimary: "--cc-text",
  textSecondary: "--cc-text-secondary",
  textMuted: "--cc-text-muted",
  accent: "--cc-accent",
  accent2: "--cc-accent-2",
  success: "--cc-success",
  warning: "--cc-warning",
  danger: "--cc-danger",
  navActive: "--cc-nav-active",
  link: "--cc-link",
  focusRing: "--cc-focus-ring",
  iconColor: "--cc-icon-color",
  fontFamily: "--cc-font-family",
  headingFont: "--cc-heading-font",
  fontScale: "--cc-font-scale",
  headingWeight: "--cc-heading-weight",
  titleSize: "--cc-title-size",
  subtitleSize: "--cc-subtitle-size",
  letterSpacing: "--cc-letter-spacing",
  lineHeight: "--cc-line-height",
  buttonTextSize: "--cc-btn-text-size",
  buttonWeight: "--cc-btn-weight",
  buttonRadius: "--cc-btn-radius",
  buttonPadY: "--cc-btn-pad-y",
  buttonPadX: "--cc-btn-pad-x",
  radius: "--cc-radius",
  borderWidth: "--cc-border-width",
  shadowStrength: "--cc-shadow-strength",
  bgImage: "--cc-bg-image",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const resolveAssetUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
};

// Apply tokens as inline CSS variables + behavioral classes on a target element.
export function applyThemeVars(tokens, el = document.documentElement) {
  if (!el || !tokens) return;
  Object.entries(TOKEN_TO_VAR).forEach(([k, cssVar]) => {
    let v = tokens[k];
    if (v === undefined || v === null || v === "") {
      el.style.removeProperty(cssVar);
      return;
    }
    if (k === "bgImage") v = `url("${resolveAssetUrl(v)}")`;
    el.style.setProperty(cssVar, String(v));
  });
  const html = document.documentElement;
  html.classList.toggle("cc-has-bg", !!(tokens.bgImage && String(tokens.bgImage).trim()));
  html.classList.toggle("cc-glass", String(tokens.glass) === "1");
  ["solid", "soft", "outline"].forEach((s) =>
    html.classList.toggle(`cc-btn-${s}`, (tokens.buttonStyle || "solid") === s)
  );
}

const FONT_OPTIONS = [
  ["'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif", "Inter (default)"],
  ["'Poppins', sans-serif", "Poppins"],
  ["'Roboto', sans-serif", "Roboto"],
  ["'Montserrat', sans-serif", "Montserrat"],
  ["'IBM Plex Sans', sans-serif", "IBM Plex Sans"],
  ["Georgia, 'Times New Roman', serif", "Serif"],
  ["'JetBrains Mono', ui-monospace, monospace", "Monospace"],
  ["system-ui, sans-serif", "System UI"],
];

// Control metadata that drives the Theme Studio form.
export const TOKEN_GROUPS = [
  {
    title: "Surfaces",
    fields: [
      { key: "canvas", label: "App background", type: "color" },
      { key: "surface", label: "Cards / panels", type: "color" },
      { key: "surface2", label: "Subtle surface", type: "color" },
      { key: "surface3", label: "Raised surface", type: "color" },
      { key: "surface4", label: "Strong surface", type: "color" },
      { key: "inputBg", label: "Input background", type: "color" },
      { key: "border", label: "Borders", type: "color" },
      { key: "cardBorder", label: "Card border (blank = use Borders)", type: "color" },
      { key: "sidebar", label: "Sidebar background", type: "color" },
      { key: "sidebarText", label: "Sidebar text", type: "color" },
    ],
  },
  {
    title: "Surface opacity & effects",
    fields: [
      { key: "canvasOpacity", label: "App background opacity", type: "range", min: 0.2, max: 1, step: 0.05 },
      { key: "surfaceOpacity", label: "Card opacity (shows image behind)", type: "range", min: 0.2, max: 1, step: 0.05 },
      { key: "sidebarOpacity", label: "Sidebar opacity", type: "range", min: 0.2, max: 1, step: 0.05 },
      { key: "glass", label: "Frosted glass (blur behind cards)", type: "toggle" },
      { key: "density", label: "Card padding / density", type: "range", min: 0.7, max: 1.3, step: 0.05, suffix: "x" },
    ],
  },
  {
    title: "Text",
    fields: [
      { key: "textPrimary", label: "Primary text", type: "color" },
      { key: "textSecondary", label: "Secondary text", type: "color" },
      { key: "textMuted", label: "Muted text", type: "color" },
    ],
  },
  {
    title: "Accent & status",
    fields: [
      { key: "accent", label: "Primary / buttons", type: "color" },
      { key: "accent2", label: "Accent gradient end", type: "color" },
      { key: "success", label: "Success", type: "color" },
      { key: "warning", label: "Warning", type: "color" },
      { key: "danger", label: "Danger", type: "color" },
    ],
  },
  {
    title: "Links & icons",
    fields: [
      { key: "navActive", label: "Active nav highlight (blank = accent)", type: "color" },
      { key: "link", label: "Link color (blank = accent)", type: "color" },
      { key: "focusRing", label: "Focus ring (blank = accent)", type: "color" },
      { key: "iconColor", label: "Icon color (blank = inherit)", type: "color" },
    ],
  },
  {
    title: "Typography",
    fields: [
      { key: "fontFamily", label: "Body font", type: "select", options: FONT_OPTIONS },
      { key: "headingFont", label: "Heading font", type: "select", options: [["", "Same as body"], ...FONT_OPTIONS] },
      { key: "fontScale", label: "Base text size", type: "range", min: 0.85, max: 1.2, step: 0.01, suffix: "x" },
      { key: "titleSize", label: "Title size", type: "range", min: 0.9, max: 2.4, step: 0.0625, unit: "rem", suffix: "rem" },
      { key: "subtitleSize", label: "Subtitle size", type: "range", min: 0.7, max: 1.4, step: 0.0625, unit: "rem", suffix: "rem" },
      { key: "headingWeight", label: "Heading weight", type: "select", options: [["500", "Medium"], ["600", "Semibold"], ["700", "Bold"], ["800", "Extra bold"]] },
      { key: "letterSpacing", label: "Letter spacing", type: "range", min: -0.03, max: 0.1, step: 0.005, unit: "em", suffix: "em" },
      { key: "lineHeight", label: "Line height", type: "range", min: 1.2, max: 1.9, step: 0.05 },
    ],
  },
  {
    title: "Buttons",
    fields: [
      { key: "buttonStyle", label: "Button style", type: "select", options: [["solid", "Solid"], ["soft", "Soft tint"], ["outline", "Outline"]] },
      { key: "buttonTextSize", label: "Button text size", type: "range", min: 0.7, max: 1.2, step: 0.0625, unit: "rem", suffix: "rem" },
      { key: "buttonWeight", label: "Button text weight", type: "select", options: [["500", "Medium"], ["600", "Semibold"], ["700", "Bold"]] },
      { key: "buttonPadY", label: "Button height (vertical pad)", type: "range", min: 0.25, max: 1, step: 0.0625, unit: "rem", suffix: "rem" },
      { key: "buttonPadX", label: "Button width (horizontal pad)", type: "range", min: 0.5, max: 2, step: 0.0625, unit: "rem", suffix: "rem" },
      { key: "buttonRadius", label: "Button corner radius", type: "range", min: 0, max: 1.5, step: 0.0625, unit: "rem", suffix: "rem" },
    ],
  },
  {
    title: "Shape & depth",
    fields: [
      { key: "radius", label: "Corner radius", type: "range", min: 0, max: 1.5, step: 0.05, unit: "rem", suffix: "rem" },
      { key: "borderWidth", label: "Border width", type: "range", min: 0, max: 3, step: 0.5, unit: "px", suffix: "px" },
      { key: "shadowStrength", label: "Shadow intensity", type: "range", min: 0, max: 1.5, step: 0.05, suffix: "x" },
    ],
  },
];

// Ready-made presets users can apply with one click then tweak.
export const THEME_PRESETS = [
  { name: "Midnight", tokens: { ...DEFAULT_TOKENS } },
  {
    name: "Slate",
    tokens: {
      ...DEFAULT_TOKENS,
      canvas: "#0F1318", surface: "#1A1F27", surface2: "#161A21", surface3: "#222831",
      surface4: "#2C333E", inputBg: "#161A21", border: "#323A46", sidebar: "#161A21", sidebarText: "#E6EBF3",
      textPrimary: "#F4F6F8", textSecondary: "#C2C9D2", textMuted: "#8B95A2",
      accent: "#6366F1", accent2: "#8B5CF6", radius: "0.5rem",
    },
  },
  {
    name: "Ocean",
    tokens: {
      ...DEFAULT_TOKENS,
      canvas: "#06141F", surface: "#0C2233", surface2: "#0A1B29", surface3: "#123349",
      surface4: "#17415C", inputBg: "#0A1B29", border: "#1C465F", sidebar: "#0A1B29", sidebarText: "#EAF6FB",
      textPrimary: "#EAF6FB", textSecondary: "#B6D6E2", textMuted: "#7CA3B4",
      accent: "#06B6D4", accent2: "#0EA5E9", success: "#14B8A6", radius: "1rem",
    },
  },
  {
    name: "Forest",
    tokens: {
      ...DEFAULT_TOKENS,
      canvas: "#0B130E", surface: "#13211A", surface2: "#101B15", surface3: "#1B3026",
      surface4: "#234035", inputBg: "#101B15", border: "#264636", sidebar: "#101B15", sidebarText: "#EDF7F0",
      textPrimary: "#EDF7F0", textSecondary: "#BFD8C8", textMuted: "#85A893",
      accent: "#22C55E", accent2: "#16A34A", warning: "#EAB308", radius: "0.625rem",
    },
  },
  {
    name: "Light",
    tokens: {
      ...DEFAULT_TOKENS,
      canvas: "#F4F6FA", surface: "#FFFFFF", surface2: "#F1F4F9", surface3: "#E7ECF3",
      surface4: "#DCE3EC", inputBg: "#FFFFFF", border: "#D7DEE8", sidebar: "#1E293B", sidebarText: "#E6EBF3",
      textPrimary: "#0F1B2D", textSecondary: "#33425A", textMuted: "#64748B",
      accent: "#2563EB", accent2: "#3B82F6", shadowStrength: "0.4",
    },
  },
];
